import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicFaq from '../../components/PublicFaq';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, ArrowRight, ShieldCheck, Mail, Loader2, Sparkles, Cpu } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (n) => `£${(n || 0).toLocaleString('en-GB')}`;

// Free (Unverified) vs Verified (paid subscription) - per Liam's brief.
const COMPARISON = [
  { label: 'Listed in the directory', free: true, paid: true },
  { label: 'Appears in search & comparison', free: 'Dormant status', paid: 'Confirmed - with priority placement' },
  { label: 'Confirmed supplier & product badge', free: false, paid: true },
  { label: 'Manage your product listings & categories', free: false, paid: true },
  { label: 'Full rich profile - logo, media, description, links', free: false, paid: true },
  { label: 'Qualified buyer introductions from our funnels', free: false, paid: true },
  { label: 'Enhanced SEO & AI-tool discoverability', free: false, paid: true },
  { label: 'Access to CTC partner services - webinars, emails, campaigns', free: false, paid: 'Choose what you want' },
];

const Cell = ({ v }) => {
  if (v === true) return <Check className="w-5 h-5 text-[#00A79D] mx-auto" data-testid="cell-yes" />;
  if (v === false) return <X className="w-5 h-5 text-gray-300 mx-auto" data-testid="cell-no" />;
  return <span className="text-xs text-gray-600">{v}</span>;
};

const GetVerifiedForm = () => {
  const [f, setF] = useState({ name: '', organisation: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) { toast.error('Email required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/leads`, {
        email: f.email.trim(), name: f.name.trim() || null,
        organisation: f.organisation.trim() || null, interest: 'subscription', source: 'pricing',
      });
      setDone(true); toast.success("Thanks - we'll be in touch to get you Confirmed.");
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid="pricing-lead-success">
      <ShieldCheck className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
      <p className="font-semibold text-gray-900">You're on the list.</p>
      <p className="text-sm text-gray-600 mt-1">We'll set up your Confirmed listing and confirm your plan.</p>
    </div>
  );

  const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="pricing-lead-form">
      <input type="text" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="pricing-lead-name" />
      <input type="text" placeholder="Company name" value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} className={field} data-testid="pricing-lead-org" />
      <input type="email" required placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="pricing-lead-email" />
      <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="pricing-lead-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Get Confirmed
      </button>
    </form>
  );
};

const Pricing = () => {
  const [bands, setBands] = useState([]);
  const [gate, setGate] = useState({});
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/subscription-bands`).then((r) => { setBands(r.data.bands || []); setGate(r.data); }).catch(() => {});
  }, []);

  return (
    <div data-testid="page-pricing">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><Cpu className="w-3.5 h-3.5" /> For tech suppliers</span>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-gray-900 leading-tight">Get confirmed. Get chosen.</h1>
          <p className="text-lg text-gray-600 mt-5 max-w-2xl mx-auto">
            Most UK care tech suppliers are already listed free by our team, with Dormant status. A Confirmed subscription unlocks the badge, priority placement,
            full product control and qualified buyer introductions - priced simply by the size of your team.
          </p>
        </div>
      </section>

      {/* Free vs Verified summary */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-[#E5E7EB] rounded-xl p-7 bg-white" data-testid="plan-free">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Free</p>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mt-1">Dormant listing</h2>
            <p className="text-sm text-gray-500 mt-2">A basic presence so buyers can find you: company name and categories. Added by our team, not through online sign-up.</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-5">£0</p>
            <ul className="mt-5 space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-gray-400 mt-0.5" /> Listed in the directory</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-gray-400 mt-0.5" /> Appears in search - marked <strong>Dormant</strong></li>
              <li className="flex items-start gap-2"><X className="w-4 h-4 text-gray-300 mt-0.5" /> No Confirmed badge, product control or introductions</li>
            </ul>
          </div>
          <div className="border-2 border-[#00A79D] rounded-xl p-7 bg-[#00A79D]/[0.03] relative" data-testid="plan-verified">
            <span className="absolute -top-3 left-7 bg-[#F5871F] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1"><Sparkles className="w-3 h-3" /> Recommended</span>
            <p className="text-xs font-semibold text-[#00857D] uppercase tracking-wide">Subscription</p>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mt-1">Confirmed</h2>
            <p className="text-sm text-gray-500 mt-2">The badge, priority, product control & partner access.</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-5">from {gbp(20)}<span className="text-base font-medium text-gray-500">/mo</span></p>
            <ul className="mt-5 space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#00A79D] mt-0.5" /> Confirmed badge in listings & comparison</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#00A79D] mt-0.5" /> Priority placement in search</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#00A79D] mt-0.5" /> Manage your products, categories & rich profile</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#00A79D] mt-0.5" /> Qualified buyer introductions + partner services</li>
            </ul>
            <a href="#plans" className="inline-flex items-center gap-1.5 text-[#00857D] font-semibold mt-5" data-testid="see-plans-link">See your price <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      {/* Tiers by company size */}
      <section id="plans" className="bg-[#F9FAFB] border-y border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold text-gray-900">Pricing by team size</h2>
            <p className="text-gray-600 mt-2">Pick the band that matches your staff team. Same features on every band.</p>
          </div>

          <div className="flex items-center justify-center gap-3 mt-6" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${!annual ? 'bg-[#00A79D] text-white' : 'bg-white border border-[#E5E7EB] text-gray-600'}`} data-testid="toggle-monthly">Monthly</button>
            <button onClick={() => setAnnual(true)} className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${annual ? 'bg-[#00A79D] text-white' : 'bg-white border border-[#E5E7EB] text-gray-600'}`} data-testid="toggle-annual">Annual <span className="text-[#F5871F]">· 1 month free</span></button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 mt-8 max-w-4xl mx-auto">
            {bands.filter((b) => !b.call_only).map((b, i) => (
              <div key={b.key} className={`bg-white border rounded-xl p-5 flex flex-col ${i === 1 ? 'border-[#00A79D] shadow-sm' : 'border-[#E5E7EB]'}`} data-testid={`tier-${b.key}`}>
                <p className="text-sm font-bold text-[#1A3A5F]">{b.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{b.staff_label} staff</p>
                <div className="mt-4">
                  <p className="text-3xl font-extrabold text-gray-900">{gbp(annual ? b.annual : b.monthly)}</p>
                  <p className="text-xs text-gray-500">{annual ? 'per year' : 'per month'}</p>
                  {annual && <p className="text-[11px] text-[#00857D] mt-1">≈ {gbp(Math.round(b.annual / 12))}/mo</p>}
                </div>
                <a href="#get-verified" className={`mt-5 text-center text-sm font-semibold px-3 py-2 rounded-md transition-colors ${i === 1 ? 'bg-[#00A79D] hover:bg-[#00857D] text-white' : 'border border-[#00A79D] text-[#00857D] hover:bg-[#00A79D]/5'}`} data-testid={`tier-cta-${b.key}`}>Choose</a>
              </div>
            ))}
            {bands.some((b) => b.call_only) && (
              <div className="bg-[#1A3A5F] text-white rounded-xl p-5 flex flex-col" data-testid="tier-larger-teams">
                <p className="text-sm font-bold">Larger teams</p>
                <p className="text-xs text-white/70 mt-0.5">{(gate.max_staff || 5) + 1}+ staff</p>
                <p className="text-sm text-white/85 mt-4 leading-relaxed flex-1">A tailored package built around your goals, from featured placement to campaigns with our care-provider audience.</p>
                {gate.sales_email && <p className="text-xs text-white/80 mt-4" data-testid="tier-larger-email">Email <a href={`mailto:${gate.sales_email}`} className="font-semibold text-white underline">{gate.sales_email}</a></p>}
                {(() => {
                  const cls = 'mt-3 inline-flex items-center justify-center gap-1.5 text-center text-sm font-semibold px-3 py-2 rounded-md bg-[#F5871F] hover:bg-[#e0730c] text-white transition-colors';
                  const url = gate.booking_url || '/book-a-call?tier=larger';
                  return url.startsWith('http')
                    ? <a href={url} target="_blank" rel="noopener noreferrer" className={cls} data-testid="tier-larger-book">Book a meeting with Sales <ArrowRight className="w-4 h-4" /></a>
                    : <Link to={url} className={cls} data-testid="tier-larger-book">Book a meeting with Sales <ArrowRight className="w-4 h-4" /></Link>;
                })()}
              </div>
            )}
          </div>
          <p className="text-center text-xs text-gray-500 mt-6">Annual plans are billed as 13 months for the price of 12 (one month free). Not sure of your band? We'll confirm it with you.</p>

          {/* Partner & Gold Partner - verification tiers (book a call) */}
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="bg-white border-2 border-[#1A3A5F] rounded-xl p-7 flex flex-col" data-testid="plan-partner">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1A3A5F] bg-[#1A3A5F]/10 px-3 py-1 rounded-full w-fit"><Sparkles className="w-3 h-3" /> Partner</span>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mt-2">Verified by CTC</h3>
              <p className="text-sm text-gray-600 mt-2 flex-1">A paid supplier whose details have been reviewed and verified by the Care Tech Compare team. Scoped on a quick call.</p>
              <Link to="/book-a-call?tier=partner" className="inline-flex items-center justify-center gap-1.5 bg-[#1A3A5F] hover:bg-[#12293f] text-white font-semibold text-sm px-5 py-2.5 rounded-md mt-4" data-testid="partner-book-cta">Book a call <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="bg-white border-2 border-[#F5871F] rounded-xl p-7 flex flex-col" data-testid="plan-gold">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#F5871F] bg-[#F5871F]/10 px-3 py-1 rounded-full w-fit"><Sparkles className="w-3 h-3" /> Gold Partner</span>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mt-2">Independently assured by Care City</h3>
              <p className="text-sm text-gray-600 mt-2 flex-1">The highest tier of trust: independently checked by our delivery partner, Care City. <strong>Assurance is earned, not bought.</strong></p>
              <Link to="/book-a-call?tier=gold" className="inline-flex items-center justify-center gap-1.5 bg-[#F5871F] hover:bg-[#e0730c] text-white font-semibold text-sm px-5 py-2.5 rounded-md mt-4" data-testid="gold-book-cta">Book a call <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Full comparison table */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-heading text-2xl font-bold text-gray-900 text-center mb-8">Free vs Confirmed - everything you get</h2>
        <div className="border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid="comparison-table">
          <div className="grid grid-cols-[1fr_100px_140px] bg-[#F9FAFB] border-b border-[#E5E7EB] text-xs font-bold text-gray-600 uppercase tracking-wide">
            <div className="px-4 py-3">Feature</div>
            <div className="px-2 py-3 text-center">Free</div>
            <div className="px-2 py-3 text-center text-[#00857D]">Confirmed</div>
          </div>
          {COMPARISON.map((row, idx) => (
            <div key={row.label} className={`grid grid-cols-[1fr_100px_140px] items-center ${idx % 2 ? 'bg-white' : 'bg-[#FCFDFD]'} border-b border-[#F1F1F1] last:border-0`}>
              <div className="px-4 py-3 text-sm text-gray-700">{row.label}</div>
              <div className="px-2 py-3 text-center"><Cell v={row.free} /></div>
              <div className="px-2 py-3 text-center"><Cell v={row.paid} /></div>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-5">No subscription means a <strong>Dormant</strong> listing (added by our team) and no access to CTC partner services. To create and manage your own listing, choose a Confirmed plan.</p>
      </section>

      {/* CTA */}
      <section id="get-verified" className="bg-[#1A3A5F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold text-white">Ready to get Confirmed?</h2>
            <p className="text-gray-300 text-sm mt-3">Tell us about your company and we'll confirm your band, set up your Confirmed listing and get you live.</p>
            <Link to="/browse" className="inline-flex items-center gap-1.5 text-[#2fd0c6] font-semibold text-sm mt-4" data-testid="pricing-browse-link">See the directory <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="bg-white rounded-xl p-6">
            <GetVerifiedForm />
          </div>
        </div>
      </section>

      <PublicFaq id="pricing" heading="Pricing FAQs"
        intro="Everything you need to know about listing your care technology product or service on Care Tech Compare."
        faqs={[
          { q: 'Is it free to list a product?', a: 'Our team adds free Dormant listings (company name and categories) for UK care tech suppliers. These are not created through online sign-up. To create and manage your own listing, with your logo, description, website and products, choose a Confirmed plan.' },
          { q: 'Do care providers pay to use Care Tech Compare?', a: 'No. Access is completely free for care providers, Local Authorities, membership organisations and advisors. The platform is funded by supplier listings, which keeps the directory independent for the people researching technology.' },
          { q: 'Can I change or cancel my plan later?', a: 'Yes. You can upgrade, downgrade or cancel your listing plan at any time from your supplier portal. Changes take effect from your next billing period and your free listing always remains.' },
          { q: 'What do I get for a premium listing?', a: 'A premium listing unlocks a full rich profile - galleries, videos, screenshots, product pages, testimonials and category ranking - plus proactive promotion in our buyer campaigns and qualified introductions from providers actively comparing solutions.' },
        ]} />
    </div>
  );
};

export default Pricing;
